// ============================================================================
// Recruiting OS — API Service Layer
// All communication with the FastAPI backend goes through here.
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

let authToken = localStorage.getItem("ros_token") || null;

// ============================================================================
// Core HTTP
// ============================================================================

async function request(path, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    authToken = null;
    localStorage.removeItem("ros_token");
    window.dispatchEvent(new Event("auth_expired"));
    throw new Error("Session expired. Please log in again.");
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  if (response.status === 204) return null;
  return response.json();
}

function get(path) {
  return request(path);
}

function post(path, body) {
  return request(path, { method: "POST", body: JSON.stringify(body) });
}

function patch(path, body) {
  return request(path, { method: "PATCH", body: JSON.stringify(body) });
}

// ============================================================================
// Auth
// ============================================================================

export async function login(email, password) {
  const data = await post("/auth/login", { email, password });
  authToken = data.access_token;
  localStorage.setItem("ros_token", authToken);
  return data;
}

export async function setupAdmin(email, password, fullName) {
  const params = new URLSearchParams({ email, password, full_name: fullName });
  const response = await fetch(`${API_BASE}/auth/setup?${params}`, { method: "POST" });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || "Setup failed");
  }
  return response.json();
}

export async function getMe() {
  return get("/auth/me");
}

export function logout() {
  authToken = null;
  localStorage.removeItem("ros_token");
}

export function isAuthenticated() {
  return !!authToken;
}

// ============================================================================
// Clients
// ============================================================================

export async function fetchClients(activeOnly = true) {
  return get(`/clients?active_only=${activeOnly}`);
}

export async function fetchClient(id) {
  return get(`/clients/${id}`);
}

export async function createClient(data) {
  return post("/clients", data);
}

export async function updateClient(id, data) {
  return patch(`/clients/${id}`, data);
}

// ============================================================================
// Roles
// ============================================================================

export async function fetchRoles(clientId = null, activeOnly = true) {
  let path = `/roles?active_only=${activeOnly}`;
  if (clientId) path += `&client_id=${clientId}`;
  return get(path);
}

export async function fetchRole(id) {
  return get(`/roles/${id}`);
}

export async function createRole(data) {
  return post("/roles", data);
}

export async function updateRole(id, data) {
  return patch(`/roles/${id}`, data);
}

// ============================================================================
// Candidates
// ============================================================================

export async function fetchCandidates({ roleId, clientId, stage, activeOnly = true } = {}) {
  let path = `/candidates?active_only=${activeOnly}`;
  if (roleId) path += `&role_id=${roleId}`;
  if (clientId) path += `&client_id=${clientId}`;
  if (stage) path += `&stage=${stage}`;
  return get(path);
}

export async function fetchCandidate(id) {
  return get(`/candidates/${id}`);
}

export async function createCandidate(data) {
  return post("/candidates", data);
}

export async function updateCandidate(id, data) {
  return patch(`/candidates/${id}`, data);
}

export async function updateCandidateStage(id, stage, notes = null, dispositionReason = null, extra = {}) {
  const body = { stage };
  if (notes) body.notes = notes;
  if (dispositionReason) body.disposition_reason = dispositionReason;
  if (extra.bench_reason) body.bench_reason = extra.bench_reason;
  if (extra.follow_up_days) body.follow_up_days = extra.follow_up_days;
  return post(`/candidates/${id}/stage`, body);
}

export async function fetchCandidateHistory(id) {
  return get(`/candidates/${id}/history`);
}

export async function fetchPipelineSummary(clientId = null) {
  let path = "/candidates/pipeline";
  if (clientId) path += `?client_id=${clientId}`;
  return get(path);
}

export async function triggerIntegrityCheck(id) {
  return post(`/candidates/${id}/integrity-check`, {});
}

export async function uploadResume(id, file) {
  const formData = new FormData();
  formData.append("file", file);
  const headers = {};
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

  const response = await fetch(`${API_BASE}/candidates/${id}/resume`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (response.status === 401) {
    authToken = null;
    localStorage.removeItem("ros_token");
    window.dispatchEvent(new Event("auth_expired"));
    throw new Error("Session expired. Please log in again.");
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Upload failed" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function downloadResume(id) {
  const headers = {};
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

  const response = await fetch(`${API_BASE}/candidates/${id}/resume`, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Download failed" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : "resume";

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ============================================================================
// Dashboard
// ============================================================================

export async function fetchDashboardStats() {
  return get("/dashboard/stats");
}

export async function fetchActivityLog(limit = 50, entityType = null, entityId = null) {
  let path = `/dashboard/activity?limit=${limit}`;
  if (entityType) path += `&entity_type=${entityType}`;
  if (entityId) path += `&entity_id=${entityId}`;
  return get(path);
}

export async function sendBriefingNow(testRecipient = null) {
  return post("/dashboard/briefing/send", testRecipient ? { test_recipient: testRecipient } : undefined);
}

export async function fetchBriefingPreview() {
  return get("/dashboard/briefing/preview");
}

// ============================================================================
// Client Reports
// ============================================================================

export async function fetchReports(clientId = null, status = null) {
  const params = new URLSearchParams();
  if (clientId) params.set("client_id", clientId);
  if (status) params.set("status", status);
  const qs = params.toString();
  return get(`/reports${qs ? `?${qs}` : ""}`);
}

export async function fetchReport(reportId) {
  return get(`/reports/${reportId}`);
}

export async function updateReportNarrative(reportId, narrative) {
  return patch(`/reports/${reportId}`, { narrative });
}

export async function approveReport(reportId) {
  return post(`/reports/${reportId}/approve`);
}

export async function discardReport(reportId) {
  return post(`/reports/${reportId}/discard`);
}

export async function generateReportNow(clientId) {
  return post(`/reports/generate/${clientId}`);
}

// ============================================================================
// Health
// ============================================================================

export async function checkHealth() {
  try {
    const response = await fetch(`${API_BASE.replace("/api/v1", "")}/health`);
    return response.ok;
  } catch {
    return false;
  }
}
// ============================================================================
// Insights (Intelligence Layer)
// ============================================================================

function insightsQuery({ clientId, start, end } = {}) {
  const params = new URLSearchParams();
  if (clientId) params.set("client_id", clientId);
  if (start) params.set("start", start);
  if (end) params.set("end", end);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchInsights(filters = {}) {
  return get(`/insights${insightsQuery(filters)}`);
}

// The client report is an HTML page behind JWT auth, so it's fetched here
// (with the token) and written into a tab the caller already opened —
// a plain link couldn't carry the Authorization header.
export async function fetchClientInsightReport(clientId, { start, end } = {}) {
  const headers = {};
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
  const qs = insightsQuery({ start, end });
  const response = await fetch(`${API_BASE}/insights/client/${clientId}/report${qs}`, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Report failed" }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.text();
}

export async function fetchRoleBenchMatches(roleId) {
  return get(`/roles/${roleId}/bench-matches`);
}
